import ClayLayout from "@clayui/layout";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useFetchExpertiseLevel from "../Hooks/useFetchExpertiseLevel";
import useFetchPersonas from "../Hooks/useFetchPersonas";
import useFetchUserEnrollments from "../Hooks/useFetchUserEnrollments";
import useQueryParams from "../Hooks/useQueryParams";
import "../index.scss";
import { getCertificationsList } from "../services/certification";
import { getSortedCoursesFilteredByFirstLesson } from "../services/course";
import { getLearningPaths } from "../services/learning-path";
import "../styles/ResourceLandingPage.scss";
import Faq from "./Certification/Faq";
import EducationResourceEmptyResults from "./Common/EducationResourceEmptyResult";
import Filter from "./Common/Filter";
import LandingPageCard from "./Common/LandingPageCard";
import Pagination from "./Common/Pagination";
import SearchBar from "./Common/SearchBar";

const ResourceLandingPage = ({ type }) => {
  const [completedCourses, setCompletedCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [educationResource, setEducationResource] = useState([]);
  const [hideCompletedCourses, setHideCompletedCourses] = useState(false);
  const [pageitems, setPageitems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExpertiseLevels, setSelectedExpertiseLevels] = useState([]);
  const [selectedPersonas, setSelectedPersonas] = useState([]);

  const { expertiseLevels } = useFetchExpertiseLevel();
  const { personas } = useFetchPersonas();
  const { t } = useTranslation();
  const { userCompleteEnrollments, userInProgressEnrollments } =
    useFetchUserEnrollments();

  const indexOfLast = currentPage * pageitems;

  const indexOfFirst = indexOfLast - pageitems;

  const fetchEducationResourceByType = {
    course: async () => {
      return await getSortedCoursesFilteredByFirstLesson();
    },

    certification: async () => {
      return await getCertificationsList();
    },

    learningPath: async () => {
      return await getLearningPaths(-1);
    },
  };

  const filteredEducationResources = useMemo(() => {
    return educationResource?.filter((item) => {
      const expertiseLevel = item.expertiseLevel?.key?.toLowerCase() || "";
      const personas =
        item.audience?.map((aud) => aud.key.toLowerCase()) || [];
      const title = (item.title || item.name || "").toLowerCase();

      const matchesCompletedCourses =
        !hideCompletedCourses || !completedCourses.includes(item.id);

      const matchesLevel =
        selectedExpertiseLevels.length === 0 ||
        selectedExpertiseLevels.map((l) => l.toLowerCase()).includes(expertiseLevel);

      const matchesPersonas =
        selectedPersonas.length === 0 ||
        personas.some((persona) =>
          selectedPersonas.map((p) => p.toLowerCase()).includes(persona)
        );

      const matchesTitle = title.includes(searchTerm.toLowerCase());

      return (
        matchesCompletedCourses &&
        matchesLevel &&
        matchesPersonas &&
        matchesTitle
      );
    });
  }, [
    completedCourses,
    educationResource,
    hideCompletedCourses,
    searchTerm,
    selectedExpertiseLevels,
    selectedPersonas,
  ]);

  const paginatedEducationResource = filteredEducationResources.slice(
    indexOfFirst,
    indexOfLast
  );

  useEffect(() => {
    const fetchData = async () => {
      if (fetchEducationResourceByType[type]) {
        const data = await fetchEducationResourceByType[type]();

        setEducationResource(data);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useQueryParams(
    selectedPersonas,
    selectedExpertiseLevels,
    setSelectedPersonas,
    setSelectedExpertiseLevels,
    hideCompletedCourses,
    setHideCompletedCourses
  );

  return (
    <>
      {type === "course" && (
        <SearchBar
          educationResourceItems={educationResource || []}
          onSearch={(term) => setSearchTerm(term)}
          type={type}
        />
      )}
      <div className="landing-page-main-container">
        {type === "course" && (
          <>
            <div className="filter-container">
              <Filter
                completedCoursesLabel={t("hide-completed-courses")}
                hideCompletedCourses={hideCompletedCourses}
                isCompletedCourses
                setHideCompletedCourses={setHideCompletedCourses}
                title={t("course-progress")}
              />
              <Filter
                items={personas}
                onChange={setSelectedPersonas}
                selectedItems={selectedPersonas}
                title={t("filter-by-personas")}
              />
              <Filter
                items={expertiseLevels}
                onChange={setSelectedExpertiseLevels}
                selectedItems={selectedExpertiseLevels}
                title={t("expertiseLevels")}
              />
            </div>
          </>
        )}
        <div className="landing-page-cards-container">
          <div className="landing-page-cards-list">
            {paginatedEducationResource?.length > 0 ? (
              paginatedEducationResource.map((educationResourceItem, index) => (
                <ClayLayout.Col className="card-layout" key={index}>
                  <LandingPageCard
                    educationResourceItem={educationResourceItem}
                    setCompletedCourses={setCompletedCourses}
                    t={t}
                    type={type}
                    userCompleteEnrollments={userCompleteEnrollments}
                    userInProgressEnrollments={userInProgressEnrollments}
                  />
                </ClayLayout.Col>
              ))
            ) : (
              <EducationResourceEmptyResults searchTerm={searchTerm} t={t} />
            )}
          </div>
          {type === "certification" && <Faq />}
          <Pagination
            currentPage={currentPage}
            pageitems={pageitems}
            setCurrentPage={setCurrentPage}
            setPageitems={setPageitems}
            totalItems={filteredEducationResources?.length}
          />
        </div>
      </div>
    </>
  );
};

export default ResourceLandingPage;
